<template>
  <v-container fluid class="visit-notes">
    <div class="visit-notes_wrapper">
      <v-card flat outlined class="visit-notes_card">
        <v-progress-linear v-if="loading" indeterminate color="primary" />
        <div class="visit-notes_card_header">
          <v-row no-gutters>
            <v-col sm="12">
              <span class="visit-notes_card_header_title">{{ $t("visits.summary.addVisitNote") }}</span>
            </v-col>
            <v-col sm="12">
              <OutlinedButton
                :text="$t('visits.summary.previewDraft')"
                size="large"
                icon="mdi-eye"
                :rounded="true"
                @onClick="goToVisitSummary"
              />
              <PrimaryButton
                className="visit-notes_card_header_btn-margin"
                :text="$t('visits.summary.signSend')"
                size="large"
                :rounded="true"
                :disabled="disabledSigning"
                @onClick="sign"
              />
            </v-col>
          </v-row>
        </div>
        <div class="visit-notes_card_patient-info">
          <VisitNotesCardHeader id="patientInfo" title="Patient’s Information" @clear="clearFields" />
          <v-row class="default-row-height">
            <v-col class="visit-notes-col">
              <BaseInput
                :errors="firstNameErrors"
                v-model="patientInfo.firstName"
                fieldName="firstName"
                :placeholder="$t('general.inputs.firstName')"
                @blur="$v.firstName.$touch()"
                @change="onPatientInfoFieldChange"
              />
            </v-col>
            <v-col class="visit-notes-col">
              <BaseInput
                :errors="lastNameErrors"
                v-model="patientInfo.lastName"
                fieldName="lastName"
                :placeholder="$t('general.inputs.lastName')"
                @blur="$v.lastName.$touch()"
                @change="onPatientInfoFieldChange"
              />
            </v-col>
            <v-col class="visit-notes-col">
              <PhoneInput
                :error-messages="phoneNumberErrors"
                v-model="patientInfo.phoneNumber"
                :placeholder="$t('general.inputs.contactNumber')"
                @blur="$v.phoneNumber.$touch()"
                @change="onContactInputChange"
              />
            </v-col>
          </v-row>
          <v-row class="default-row-height adaptive-row">
            <v-col col="8" class="visit-notes-col adaptive-row__genders">
              <Switcher
                :items="$t('general.genders')"
                v-model="patientInfo.gender"
                fieldName="gender"
                @change="onPatientInfoFieldChange"
              />
            </v-col>
            <v-col cols="4" class="visit-notes-col adaptive-row__state">
              <StateSelect
                :errors="stateErrors"
                v-model="patientInfo.state"
                :placeholder="$t('general.inputs.state')"
                fieldName="state"
                @blur="$v.state.$touch()"
                @change="onPatientInfoFieldChange"
              />
            </v-col>
          </v-row>
        </div>
        <VisitNotesMedicalInfo />
        <EncounterNotes />
        <div class="visit-notes_card_footer">
          <div class="visit-notes_card_footer_btns">
            <OutlinedButton
              :text="$t('visits.summary.previewDraft')"
              size="large"
              icon="mdi-eye"
              :rounded="true"
              @onClick="goToVisitSummary"
            />
            <PrimaryButton
              :text="$t('visits.summary.signSend')"
              className="visit-notes_card_header_btn-margin"
              size="large"
              :rounded="true"
              :disabled="disabledSigning"
              @onClick="sign"
            />
          </div>
        </div>
      </v-card>
    </div>
  </v-container>
</template>
<script>
import { mask } from "vue-the-mask";
import { required } from "vuelidate/lib/validators";
import { mapActions, mapGetters, mapMutations } from "vuex";

import OutlinedButton from "@/components/uikit/OutlinedButton";
import PrimaryButton from "@/components/uikit/PrimaryButton";

import PhoneInput from "../shared/PhoneInput";
import StateSelect from "../shared/StateSelect";
import BaseInput from "../uikit/BaseInput";
import Switcher from "../uikit/Switcher";
import VisitNotesCardHeader from "./CardHeader.vue";
import EncounterNotes from "./EncounterNotes";
import VisitNotesMedicalInfo from "./MedicalInfo.vue";

export default {
  name: "VisitNotes",
  components: {
    VisitNotesCardHeader,
    VisitNotesMedicalInfo,
    BaseInput,
    PhoneInput,
    Switcher,
    StateSelect,
    EncounterNotes,
    PrimaryButton,
    OutlinedButton,
  },
  validations: {
    state: { required },
    firstName: { required },
    lastName: { required },
    phoneNumber: { required },
  },
  directives: {
    mask,
  },
  props: {
    loading: Boolean,
  },
  computed: {
    ...mapGetters("visitNote", ["patientInfo", "disabledSigning"]),
    firstName() {
      return this.patientInfo.firstName;
    },
    lastName() {
      return this.patientInfo.lastName;
    },
    state() {
      return this.patientInfo.state;
    },
    phoneNumber() {
      return this.patientInfo.phoneNumber;
    },
    stateErrors() {
      const errors = [];
      if (!this.$v.state.$dirty) return errors;
      !this.$v.state.required &&
        errors.push(this.$t("validation.required", { field: this.$t("general.inputs.state") }));
      return errors;
    },
    firstNameErrors() {
      const errors = [];
      if (!this.$v.firstName.$dirty) return errors;
      !this.$v.firstName.required &&
        errors.push(this.$t("validation.required", { field: this.$t("general.inputs.firstName") }));
      return errors;
    },
    lastNameErrors() {
      const errors = [];
      if (!this.$v.lastName.$dirty) return errors;
      !this.$v.lastName.required &&
        errors.push(this.$t("validation.required", { field: this.$t("general.inputs.lastName") }));
      return errors;
    },
    phoneNumberErrors() {
      const errors = [];
      if (!this.$v.phoneNumber.$dirty) return errors;
      !this.$v.phoneNumber.required &&
        errors.push(this.$t("validation.required", { field: this.$t("general.inputs.phone") }));
      return errors;
    },
  },
  methods: {
    ...mapActions("visitNote", [
      "savePatientInfo",
      "saveVisitNote",
      "clearPatientInfoFields",
      "saveVisitNote",
      "autosave",
    ]),
    ...mapMutations("visitNote", ["setPatientInfoField"]),
    onPatientInfoFieldChange(value, fieldName) {
      this.setPatientInfoField({ value, fieldName });
      this.autosave();
    },
    onContactInputChange(phoneNumber) {
      this.setPatientInfoField({ value: phoneNumber, fieldName: "phoneNumber" });
      this.autosave();
    },
    clearFields() {
      this.clearPatientInfoFields();
      this.$v.$reset();
      this.autosave();
    },
    goToVisitSummary() {
      this.$emit("previewDraft");
    },
    sign() {
      this.$emit("sign");
    },
  },
};
</script>

<style lang="scss" scoped>
.adaptive-row {
  @media (max-width: 480px) {
    flex-direction: column;
  }
  &__genders {
    @media (max-width: 480px) {
      max-width: 100%;
    }
  }
  &__state {
    @media (max-width: 480px) {
      max-width: 60%;
    }
  }
}
</style>
